import React, { useState, useEffect } from 'react';
import { 
  Brain, Loader2, Search, Download, Users
} from 'lucide-react';
import { temperamentAPI, groupsAPI } from '../services/api';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';

const TEMPERAMENT_TYPES = {
  choleric: { name: 'Xolerik', color: '#ef4444' },
  sanguine: { name: 'Sangvinik', color: '#f59e0b' },
  phlegmatic: { name: 'Flegmatik', color: '#10b981' },
  melancholic: { name: 'Melanxolik', color: '#6366f1' }
};

const TemperamentPage = () => {
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterType, setFilterType] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchResults();
  }, [filterGroup, filterType, currentPage]);

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getAll();
      setGroups(response.data.results || response.data || []);
    } catch (error) {
      console.error('Guruhlarni yuklashda xato:', error);
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (filterGroup) params.group = filterGroup;
      if (filterType) params.type = filterType;
      if (searchTerm) params.search = searchTerm;
      
      const response = await temperamentAPI.getResults(params);
      const data = response.data;
      if (data.results) {
        setResults(data.results);
        setTotalCount(data.count || data.results.length);
      } else {
        setResults([]);
        setTotalCount(0);
      }
      setStats(data.stats || null);
    } catch (error) {
      console.error('Natijalarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchResults();
  };

  const exportToExcel = () => {
    const data = results.map(r => ({
      'Talaba ID': r.student_id,
      'F.I.Sh': r.student_name,
      'Guruh': r.group_name || '-',
      'Xolerik': r.choleric_score,
      'Sangvinik': r.sanguine_score,
      'Flegmatik': r.phlegmatic_score,
      'Melanxolik': r.melancholic_score,
      'Asosiy tip': r.dominant_type_display,
      'Sana': new Date(r.completed_at).toLocaleDateString('uz-UZ')
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Temperament');
    XLSX.writeFile(wb, 'temperament_natijalar.xlsx');
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  if (loading && results.length === 0) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Brain size={28} /> Temperament test natijalari</h1>
          <p>Talabalar psixologik test natijalari</p>
        </div>
        <button className="btn btn-success" onClick={exportToExcel} disabled={results.length === 0}>
          <Download size={18} />
          Excel export
        </button>
      </header>

      {/* Statistika */}
      {stats && (
        <div className="temperament-stats">
          <div className="temp-stat-card total">
            <Users size={24} />
            <div>
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Jami</span>
            </div>
          </div>
          {Object.entries(TEMPERAMENT_TYPES).map(([key, info]) => (
            <div key={key} className="temp-stat-card" style={{ '--temp-color': info.color }}>
              <div className="temp-stat-icon" style={{ background: info.color }}>
                {info.name[0]}
              </div>
              <div>
                <span className="stat-value">{stats[key]}</span>
                <span className="stat-label">{info.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filterlar */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Talabani qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <select 
          className="filter-select"
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
        >
          <option value="">Barcha guruhlar</option>
          {groups.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select 
          className="filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">Barcha tiplar</option>
          {Object.entries(TEMPERAMENT_TYPES).map(([key, info]) => (
            <option key={key} value={key}>{info.name}</option>
          ))}
        </select>
      </div>

      {/* Jadval */}
      {results.length === 0 ? (
        <div className="empty-state">
          <Brain size={48} />
          <p>Natijalar topilmadi</p>
        </div>
      ) : (
        <>
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{width: '50px'}}>№</th>
                  <th>Talaba</th>
                  <th>Guruh</th>
                  <th style={{width: '70px', textAlign: 'center', color: '#ef4444'}}>Xol</th>
                  <th style={{width: '70px', textAlign: 'center', color: '#f59e0b'}}>San</th>
                  <th style={{width: '70px', textAlign: 'center', color: '#10b981'}}>Fle</th>
                  <th style={{width: '70px', textAlign: 'center', color: '#6366f1'}}>Mel</th>
                  <th>Natija</th>
                  <th>Sana</th>
                </tr>
              </thead>
              <tbody>
                {results.map((result, idx) => (
                  <tr key={result.id}>
                    <td style={{color: '#94a3b8', textAlign: 'center'}}>{startIndex + idx + 1}</td>
                    <td>
                      <div className="student-info-cell">
                        <strong>{result.student_name}</strong>
                        <span className="student-id-small">{result.student_id}</span>
                      </div>
                    </td>
                    <td><span className="group-badge-small">{result.group_name || '-'}</span></td>
                    <td style={{textAlign: 'center', fontWeight: 700, color: '#ef4444', background: '#fef2f2'}}>{result.choleric_score}</td>
                    <td style={{textAlign: 'center', fontWeight: 700, color: '#f59e0b', background: '#fffbeb'}}>{result.sanguine_score}</td>
                    <td style={{textAlign: 'center', fontWeight: 700, color: '#10b981', background: '#ecfdf5'}}>{result.phlegmatic_score}</td>
                    <td style={{textAlign: 'center', fontWeight: 700, color: '#6366f1', background: '#eef2ff'}}>{result.melancholic_score}</td>
                    <td>
                      <span 
                        className="temperament-badge"
                        style={{ 
                          background: TEMPERAMENT_TYPES[result.dominant_type]?.color,
                          color: 'white'
                        }}
                      >
                        {result.dominant_type_display}
                      </span>
                    </td>
                    <td style={{color: '#64748b', fontSize: '13px'}}>{new Date(result.completed_at).toLocaleDateString('uz-UZ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
        </>
      )}
    </div>
  );
};

export default TemperamentPage;
